import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Heart, Search, ShoppingBag, Menu, X, Sparkles, User, LogOut } from "lucide-react";
import { LOGO_URL, CATEGORIES, api } from "../lib/api";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { buildRedirectQuery } from "../lib/redirect";

export default function Header() {
  const [annDismissed, setAnnDismissed] = useState(() => localStorage.getItem("ann_dismissed") === "1");
  const [announcement, setAnnouncement] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const { count } = useWishlist();
  const { user, logout } = useAuth();
  const { open: openCart, count: cartCount } = useCart();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownTimeoutRef = useRef(null);
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get("/settings/announcement").then((r) => setAnnouncement(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const dismissAnn = () => {
    localStorage.setItem("ann_dismissed", "1");
    setAnnDismissed(true);
  };

  const onSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      nav(`/busca?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchOpen(false);
    }
  };

  const showAnn = announcement && announcement.active && !annDismissed && announcement.text;

  return (
    <header className="sticky top-0 z-50 bg-white">
      {showAnn && (
        <div className="bg-[#1A1A1A] text-white relative" data-testid="announcement-bar">
          <div className="max-w-7xl mx-auto px-6 py-2.5 text-center overline text-white/90">
            {announcement.text}
          </div>
          <button
            data-testid="close-announcement"
            onClick={dismissAnn}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-1"
            aria-label="Fechar"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className={`border-b transition-all duration-300 ${scrolled ? "border-[#E8E4DF]" : "border-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[60px] md:h-[72px] flex items-center justify-between">
          {/* mobile menu button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden p-2 -ml-2"
            onClick={() => setMobileOpen(true)}
            aria-label="Menu"
          >
            <Menu size={22} />
          </button>

          {/* logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center md:flex-none md:mr-8 mx-auto md:mx-0">
            <img src={LOGO_URL} alt="Prize Joias" className="h-8 md:h-9 w-auto select-none" draggable={false} />
          </Link>

          {/* desktop nav */}
          <nav className="hidden md:flex items-center gap-8 flex-1 justify-center">
            <div
              className="relative"
              onMouseEnter={() => {
                if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
                setDropdownOpen(true);
              }}
              onMouseLeave={() => {
                dropdownTimeoutRef.current = setTimeout(() => setDropdownOpen(false), 250);
              }}
            >
              <button data-testid="nav-joias" className="overline hover:text-[#B8A88A] transition-colors py-2">
                JOIAS
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[520px] bg-white border border-[#E8E4DF] shadow-sm">
                  <div className="grid grid-cols-2 gap-8 p-8">
                    <div>
                      <p className="overline text-[#A8A8A8] mb-4">Categorias</p>
                      <ul className="space-y-2.5">
                        {CATEGORIES.map((c) => (
                          <li key={c.slug}>
                            <Link
                              data-testid={`dropdown-cat-${c.slug}`}
                              to={`/joias/${encodeURIComponent(c.slug)}`}
                              className="text-sm hover:text-[#B8A88A] transition-colors"
                            >
                              {c.label}
                            </Link>
                          </li>
                        ))}
                        <li className="pt-2 border-t border-[#E8E4DF]">
                          <Link data-testid="dropdown-cat-all" to="/joias/todos" className="text-sm text-[#B8A88A] hover:text-[#8C7B5E]">
                            Ver todas as peças →
                          </Link>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-[#FAF9F7] p-4 -m-4">
                      <p className="font-display text-lg mb-2 leading-tight">Última Curadoria</p>
                      <p className="text-xs text-[#6B6B6B] mb-3">Peças únicas que acabam de chegar</p>
                      <Link to="/novidades" className="overline text-[#B8A88A] hover:text-[#8C7B5E]">Ver coleção →</Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Link data-testid="nav-tryon" to="/try-on" className="overline hover:text-[#B8A88A] transition-colors flex items-center gap-1.5">
              <Sparkles size={12} className="text-[#B8A88A]" />
              TRY ON
            </Link>
            <Link data-testid="nav-novidades" to="/novidades" className="overline hover:text-[#B8A88A] transition-colors">
              NOVIDADES
            </Link>
            <Link data-testid="nav-sobre" to="/sobre" className="overline hover:text-[#B8A88A] transition-colors">
              SOBRE NÓS
            </Link>
          </nav>

          {/* icons */}
          <div className="flex items-center gap-4 md:gap-5">
            <button data-testid="search-button" onClick={() => setSearchOpen(true)} aria-label="Buscar">
              <Search size={20} className="hover:text-[#B8A88A] transition-colors" />
            </button>
            <Link to="/wishlist" data-testid="wishlist-link" className="relative">
              <Heart size={20} className="hover:text-[#B8A88A] transition-colors" />
              {count > 0 && (
                <span data-testid="wishlist-count" className="absolute -top-2 -right-2 bg-[#B8A88A] text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </Link>
            {user ? (
              <div className="relative">
                <button
                  data-testid="user-menu-button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2"
                  aria-label="Menu da conta"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <User size={20} className="hover:text-[#B8A88A]" />
                  )}
                </button>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-[#E8E4DF] shadow-sm z-50">
                      <div className="p-4 border-b border-[#E8E4DF]">
                        <p className="text-xs text-[#A8A8A8] mb-1">Olá,</p>
                        <p className="font-display text-base truncate">{user.name || user.email}</p>
                      </div>
                      <Link to="/wishlist" onClick={() => setUserMenuOpen(false)} className="block px-4 py-3 text-sm hover:bg-[#FAF9F7]" data-testid="menu-wishlist">
                        Minha Wishlist
                      </Link>
                      <Link to="/gift-card" onClick={() => setUserMenuOpen(false)} className="block px-4 py-3 text-sm hover:bg-[#FAF9F7]" data-testid="menu-giftcard">
                        Gift Cards
                      </Link>
                      <button
                        data-testid="logout-btn"
                        onClick={() => { setUserMenuOpen(false); logout(); }}
                        className="w-full text-left px-4 py-3 text-sm hover:bg-[#FAF9F7] flex items-center gap-2 text-[#6B6B6B]"
                      >
                        <LogOut size={14}/> Sair
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link
                data-testid="login-btn"
                to={`/login${buildRedirectQuery(location)}`}
                className="overline hidden md:inline-flex items-center gap-1.5 hover:text-[#B8A88A] transition-colors"
              >
                <User size={14}/> Entrar
              </Link>
            )}
            <button
              data-testid="bag-button"
              onClick={openCart}
              className="hidden md:inline-flex relative items-center justify-center w-9 h-9 hover:text-[#B8A88A] transition-colors"
              aria-label={`Abrir carrinho (${cartCount} ${cartCount === 1 ? "item" : "itens"})`}
            >
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span
                  data-testid="bag-badge"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#B8A88A] text-white text-[10px] font-medium rounded-full flex items-center justify-center"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* search overlay */}
      {searchOpen && (
        <div data-testid="search-overlay" className="fixed inset-0 bg-white z-[100] flex flex-col">
          <div className="border-b border-[#E8E4DF] px-6 py-4 flex items-center justify-between">
            <span className="overline">Buscar</span>
            <button onClick={() => setSearchOpen(false)} data-testid="search-close">
              <X size={20} />
            </button>
          </div>
          <form onSubmit={onSearch} className="max-w-3xl mx-auto w-full px-6 pt-16">
            <input
              autoFocus
              data-testid="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Busque por marca, peça, pedra..."
              className="w-full text-3xl md:text-5xl font-display border-b border-[#E8E4DF] py-4 focus:outline-none focus:border-[#B8A88A]"
            />
            <p className="text-xs text-[#A8A8A8] mt-3 overline">Pressione Enter para buscar</p>
          </form>
        </div>
      )}

      {/* mobile menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="fixed inset-0 bg-white z-[100] md:hidden flex flex-col">
          <div className="flex items-center justify-between px-6 h-[60px] border-b border-[#E8E4DF] flex-shrink-0">
            <span className="overline">Menu</span>
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" className="p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <X size={22} />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-8 space-y-1">
            <p className="overline text-[#A8A8A8] mb-3">Categorias</p>
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/joias/${encodeURIComponent(c.slug)}`}
                className="block py-3 font-display text-2xl border-b border-[#E8E4DF] min-h-[44px]"
                data-testid={`mobile-cat-${c.slug}`}
              >
                {c.label}
              </Link>
            ))}
            {/* "Ver todas as peças" — primary catalogue entry; mirrors the
                desktop dropdown's "Ver todas as peças →" link. */}
            <Link
              to="/joias/todos"
              className="block py-3 font-display text-xl text-[#B8A88A] hover:text-[#8C7B5E] min-h-[44px]"
              data-testid="mobile-cat-all"
            >
              Ver todas as peças →
            </Link>
            <div className="pt-6 space-y-3">
              <Link to="/try-on" className="block font-display text-2xl py-2 min-h-[44px]" data-testid="mobile-nav-tryon">
                <Sparkles size={14} className="inline mr-2 text-[#B8A88A]" />
                Try On Experience
              </Link>
              <Link to="/novidades" className="block font-display text-2xl py-2 min-h-[44px]" data-testid="mobile-nav-novidades">Novidades</Link>
              <Link to="/presentes" className="block font-display text-2xl py-2 min-h-[44px]" data-testid="mobile-nav-presentes">Presentes</Link>
              <Link to="/sobre" className="block font-display text-2xl py-2 min-h-[44px]" data-testid="mobile-nav-sobre">Sobre Nós</Link>
            </div>
          </nav>
          {/* Sticky bottom CTA — Entrar / Cadastre (logged-out) OR account
              shortcut (logged-in). Always visible above the fold so visitors
              on small phones can reach it without scrolling. min-h 48px to
              comfortably exceed the 44px tap-target guideline. */}
          <div className="flex-shrink-0 border-t border-[#E8E4DF] p-5 bg-white">
            {user ? (
              <div className="space-y-3">
                <p className="text-xs text-[#A8A8A8]">Olá, <span className="text-[#1A1A1A]">{user.name || user.email}</span></p>
                <Link
                  to="/wishlist"
                  className="block w-full text-center py-3 border border-[#1A1A1A] text-sm uppercase tracking-wider min-h-[48px] flex items-center justify-center"
                  data-testid="mobile-account-wishlist"
                >
                  Minha Wishlist
                </Link>
                <button
                  type="button"
                  onClick={() => { setMobileOpen(false); logout(); }}
                  className="block w-full text-center py-3 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] inline-flex items-center justify-center gap-2 min-h-[48px]"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut size={14} /> Sair
                </button>
              </div>
            ) : (
              <Link
                to={`/login${buildRedirectQuery(location)}`}
                className="block w-full text-center bg-[#B8A88A] hover:bg-[#8C7B5E] text-white py-4 text-sm uppercase tracking-[0.15em] min-h-[48px] flex items-center justify-center"
                style={{ fontFamily: "'Jost', sans-serif" }}
                data-testid="mobile-login-btn"
              >
                Entrar / Cadastre-se
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
